import { UnorderedListOutlined } from "@ant-design/icons";
import { Button, Table, Tag, Tooltip } from "antd";
import React, { useCallback, useMemo, useState } from 'react';
import InputSearch from "../../../components/Basic/InputSearch";
import Icon, { ICON_NAME } from "../../../components/Icon";
import Menu from "../../../components/Menu";
import { openPopup2, POPUP_TYPE } from '../../../contexts/PopupContext';
import { useSelector } from "../../../util/store/store";
import { ACTION_TYPE, getRoleNames, userRole } from "../../../util/varibles/constants";
import { IIdName } from "../../../util/varibles/interface";
import { IUser } from "../../LoginPage/constants";
import ChangePassword from "../_component/ChangePassword";
import PopupEditUser from "../PopupEditUser";
import styles from '../style.module.scss';
import DeleteUser from "./DeleteUser";

interface IProps {
    openInfo(key: string, list: any): void
}

const defaultFilter = { customers: [], roles: [] };

const getUserList = (users: IUser[], value: string) => {
    const searchValue = value.toLowerCase();
    const { list, customers, roles } = users.reduce((rs: any, item: any) => {
        if ((item.email || '').toLowerCase().includes(searchValue)) {
            rs.list.push(item);
            rs.customers[item.tenant_id] = item.tenant_name;
            item.roles.forEach((sub: string) => rs.roles[sub] = true);
        }
        return rs;
    }, { list: [], customers: {}, roles: {} });

    return {
        list,
        filter: {
            customers: Object.keys(customers).reduce((list: any, key) => [...list, {
                text: customers[key],
                value: key
            }], []).sort((a: any, b: any) => a.text.localeCompare(b.text)),
            roles: Object.keys(userRole).reduce((list: any, key: any) => roles[key] ? [...list, {
                text: getRoleNames([key]),
                value: key
            }] : list, []),
        }
    };
};

const Users: React.FC<IProps> = ({ openInfo }) => {
    const customers = useSelector(state => state.user.customers);
    const sites = useSelector(state => state.user.sites);
    const factories = useSelector(state => state.user.factories);
    const users = useSelector(state => state.user.users);
    const loadingUser = useSelector(state => state.user.loadingUser);
    const [name, setName] = useState('');

    const { list, filter = defaultFilter } = useMemo(() => getUserList(users, name), [users, name]);

    const handleFilter = useCallback((value: string) => {
        setName(value);
    }, []);

    const changePassword = useCallback((data: any) => {
        openPopup2(<ChangePassword user={data} />, {
            modalProps: {
                title: `Change password ${data.email}`
            }
        });
    }, []);

    const handleEdit = useCallback((actionType: ACTION_TYPE, data?: IUser) => {
        openPopup2(<PopupEditUser
            editMode={actionType}
            user={data}
        />, {
            popupType: POPUP_TYPE.DRAWER,
            modalProps: {
                title: actionType === ACTION_TYPE.CREATE ? 'Add user' : `Update user ${data?.email}`,
                width: "30%"
            }
        });
    }, []);

    const columns = useMemo(() => [
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Roles',
            dataIndex: 'roles',
            key: 'roles',
            filters: filter.roles,
            filterIcon: (filtered: boolean) => <Icon icon={ICON_NAME.FILTER} dataAttribute={{ 'data-on': filtered }} />,
            onFilter: (value: any, record: any) => new Set(record.roles).has(value),
            render: (roles: any) => <div className={styles.roles}>
                {(roles || []).map((role: any) => {
                    let color = "geekblue";
                    if (role === 'admin') {
                        color = 'volcano';
                    }
                    return <Tag className='m-0' color={color} key={role}>
                        {getRoleNames([role])}
                    </Tag>;
                })}
            </div>
        },
        {
            title: 'Regions',
            key: 'regions',
            render: (data: any) => {
                const { regions, tenant_id } = data;
                if ((regions || []).length === 0)
                    return null;

                const regionsIds = new Set(regions);
                const customer = customers.find((item: any) => item.tenant_id === tenant_id);
                if (!customer)
                    return null;

                const { regions: regionsOrigin = [] } = customer;
                const dataOfRegions: any = [];
                for (let i = 0; i < regionsOrigin.length; i++) {
                    const { id } = regionsOrigin[i];
                    if (regionsIds.has(id)) {
                        regionsIds.delete(id);
                        dataOfRegions.push(regionsOrigin[i]);
                    }
                    if (regionsIds.size === 0)
                        break;
                }

                return (dataOfRegions || []).map((item: any) => item.name).join(', ');
            },
        },
        {
            title: 'Customer',
            dataIndex: 'tenant_name',
            key: 'tenant_name',
            filterIcon: (filtered: boolean) => <Icon icon={ICON_NAME.FILTER} dataAttribute={{ 'data-on': filtered }} />,
            filters: filter.customers,
            onFilter: (value: any, record: any) => record.tenant_id.indexOf(value) !== -1,
            width: 140
        },
        {
            title: 'Vessel',
            key: 'vessels',
            width: 100,
            render: (data: any) => {
                const { vessels, tenant_id } = data;
                if ((vessels || []).length === 0)
                    return null;

                const vesselsIds = new Set(vessels);
                const customer = customers.find((item: any) => item.tenant_id === tenant_id);
                if (!customer)
                    return null;

                const { vessels: vesselsOrigin = [] } = customer;
                const dataOfVessels: any = [];
                for (let i = 0; i < vesselsOrigin.length; i++) {
                    const { id } = vesselsOrigin[i];
                    if (vesselsIds.has(id)) {
                        vesselsIds.delete(id);
                        dataOfVessels.push(vesselsOrigin[i]);
                    }
                    if (vesselsIds.size === 0)
                        break;
                }

                return <div className={styles['col-vessels']}>
                    <div><Tooltip
                        title={dataOfVessels.map((item: any) => item.name).join(', ')}>{vessels.length}</Tooltip>
                    </div>
                    <Button
                        size='small'
                        icon={<UnorderedListOutlined />}
                        onClick={() => openInfo('vessel', dataOfVessels)}
                    />
                </div>;
            },
        },
        {
            title: 'Sites',
            key: 'sites',
            width: 100,
            render: ({ sites: siteList, tenant_id }: { sites: string[], tenant_id: string }) => {
                if ((siteList || []).length === 0)
                    return null;

                const sitesIds = new Set(siteList.map(item => [item, tenant_id].join('_')));
                const dataOfSites: IIdName[] = [];
                for (let i = 0; i < sites.length; i++) {
                    const { id, tenant_id } = sites[i];
                    const key = [id, tenant_id].join('_');
                    if (sitesIds.has(key)) {
                        sitesIds.delete(key);
                        dataOfSites.push(sites[i]);
                    }
                    if (sitesIds.size === 0)
                        break;
                }

                return <div className={styles['col-vessels']}>
                    <div><Tooltip title={dataOfSites.map((item) => item.name).join(', ')}>{siteList.length}</Tooltip>
                    </div>
                    <Button
                        size='small'
                        icon={<UnorderedListOutlined />}
                        onClick={() => openInfo('site', dataOfSites)}
                    />
                </div>;
            },
        },
        {
            title: 'Factories',
            dataIndex: 'factories',
            key: 'factories',
            width: 100,
            render: (factoryList: any) => {
                if ((factoryList || []).length === 0)
                    return null;

                const factoriesIds = new Set(factoryList);
                const dataOfFactories: any = [];
                for (let i = 0; i < factories.length; i++) {
                    const { id } = factories[i];
                    if (factoriesIds.has(id)) {
                        factoriesIds.delete(id);
                        dataOfFactories.push(factories[i]);
                    }
                    if (factoriesIds.size === 0)
                        break;
                }

                return <div className={styles['col-vessels']}>
                    <div>
                        <Tooltip title={dataOfFactories.map((item: any) => item.name).join(', ')}>
                            {factoryList.length}
                        </Tooltip>
                    </div>
                    <Button
                        size='small'
                        icon={<UnorderedListOutlined />}
                        onClick={() => openInfo('factory', dataOfFactories)}
                    />
                </div>;
            },
        },
        {
            width: 60,
            render: (data: any) => <Menu
                items={[
                    {
                        key: 'change-password',
                        label: <div className='menu-line' onClick={() => changePassword(data)}>
                            <Icon icon={ICON_NAME.LOCK} />
                            <div>Change password</div>
                        </div>
                    },
                    {
                        key: 'edit',
                        label: <div
                            className='menu-line'
                            onClick={() => handleEdit(ACTION_TYPE.EDIT, data)}
                        >
                            <Icon icon={ICON_NAME.EDIT} />
                            <div>Edit user</div>
                        </div>
                    },
                    {
                        key: 'delete',
                        disabled: true,
                        danger: true,
                        className: "!cursor-pointer menu-line menu-active",
                        label: <DeleteUser data={data} />
                    }
                ]}
                placement='bottomRight'
            >
                <div className='three-dot' />
            </Menu>
        }
    ], [changePassword, customers, factories, filter, handleEdit, openInfo, sites]);

    return <div className={styles.slide}>
        <div className={styles['container-action-tab']}>
            <InputSearch
                placeHolder='Search by name'
                value={name}
                onChange={handleFilter}
            />
            <Tooltip title="Add user">
                <Button
                    className='bt-primary bt-plus'
                    shape="circle"
                    onClick={() => handleEdit(ACTION_TYPE.CREATE)}
                />
            </Tooltip>
        </div>
        <div className={styles.list}>
            <Table
                loading={loadingUser}
                pagination={false}
                columns={columns}
                dataSource={list}
                rowKey='username'
            />
        </div>
    </div>;
};

export default Users;
